import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Modal, Upload, Select, Input } from 'antd';
import { EyeOutlined, UploadOutlined, PaperClipOutlined, UnorderedListOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { isAdmin } from '../../../utils/userRoles';
import { uploadFile } from '../../../utils/uploadFile';
import '../../order/orders/Orders.css';
import './MyTasks.css';

const MAX_WORK_RESULT_FILES = 10;
const MAX_FILE_SIZE_MB = 2;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'done', label: 'Done' },
];

function isHtml(str) {
  if (typeof str !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(str);
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
function isImageAttachment(att) {
  if (!att?.url) return false;
  if (att.url.startsWith('data:image/')) return true;
  return att.name && IMAGE_EXTENSIONS.test(att.name);
}

function openAttachmentUrl(url) {
  if (!url || typeof url !== 'string') return;
  if (url.startsWith('data:')) {
    try {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch(() => window.open(url, '_blank', 'noopener,noreferrer'));
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export default function MyTasks({ embedded = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.authReducer?.user);
  const isAdminUser = isAdmin(user);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [viewTask, setViewTask] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitWorkTask, setSubmitWorkTask] = useState(null);
  const [workResult, setWorkResult] = useState([]);
  const [workDescription, setWorkDescription] = useState('');
  const [submitWorkLoading, setSubmitWorkLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const fetchTasks = useCallback(async (page, limit) => {
    setLoading(true);
    try {
      const res = await axios.get('/tasks/my', { params: { page, limit } });
      setTasks(res.data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        current: res.data?.page ?? page,
        pageSize: res.data?.limit ?? limit,
        total: res.data?.total ?? 0,
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load tasks';
      dispatch(setMessage({ message: msg, type: 'error' }));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchTasks(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchTasks]);

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const openSubmitWork = (task) => {
    setSubmitWorkTask(task);
    setWorkResult([]);
    setWorkDescription('');
  };

  const handleStatusChange = async (taskId, newStatus, record) => {
    if (!taskId) return;
    setStatusUpdatingId(taskId);
    try {
      await axios.patch(`/tasks/${taskId}/status`, { status: newStatus });
      dispatch(setMessage({ message: 'Status updated', type: 'success' }));
      fetchTasks(pagination.current, pagination.pageSize);
      if (newStatus === 'done' && record && record.taskType === 'one-time') {
        openSubmitWork(record);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleSubmitWork = async () => {
    if (!submitWorkTask) return;
    setSubmitWorkLoading(true);
    try {
      await axios.post('/task-completions', {
        taskId: submitWorkTask._id,
        workResult: workResult.map((a) => ({ name: a.name, url: a.url })),
        description: workDescription.trim() || undefined,
      });
      dispatch(setMessage({ message: 'Work submitted. Admin will verify and assign reward points.', type: 'success' }));
      // Server sets task status to 'done' for one-time tasks; update list so status shows Done immediately
      setTasks((prev) =>
        prev.map((t) => (t._id === submitWorkTask._id && t.taskType === 'one-time' ? { ...t, status: 'done' } : t))
      );
      setSubmitWorkTask(null);
      setWorkResult([]);
      setWorkDescription('');
      fetchTasks(pagination.current, pagination.pageSize);
    } catch (err) {
      const msg = err.response?.data?.error || 'Submit failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitWorkLoading(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title) => <span className="my-tasks-cell-title">{title || '—'}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 90,
      render: (v) => (v === 'recurring' ? 'Recurring' : 'One Time'),
    },
    {
      title: 'Reward',
      dataIndex: 'rewardPoint',
      key: 'rewardPoint',
      width: 90,
      render: (val) => <Tag color="gold">{val} pts</Tag>,
    },
    {
      title: 'Completed date',
      dataIndex: 'completedDate',
      key: 'completedDate',
      width: 120,
      render: (val) => formatDate(val),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        record.taskType === 'recurring' ? (
          '—'
        ) : (
        <Select
          value={status || 'open'}
          options={STATUS_OPTIONS}
          onChange={(value) => handleStatusChange(record._id, value, record)}
          loading={statusUpdatingId === record._id}
          size="small"
          style={{ minWidth: 110 }}
          dropdownMatchSelectWidth={false}
        />
        )
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (val) => formatDateTime(val),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewTask(record)}
          >
          </Button>
          {!isAdminUser && (
            <Button
              type="text"
              size="small"
              icon={<UploadOutlined />}
              onClick={() => openSubmitWork(record)}
              title="Submit work"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="my-tasks-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!embedded && (
          <div className="orders-header">
            <div className="orders-header-title-row">
              <Title level={3} style={{ margin: 0 }}>
                My Tasks
              </Title>
              <div className="orders-header-actions">
                <Button
                  type="primary"
                  icon={<UnorderedListOutlined />}
                  onClick={() => navigate('/my-completions')}
                  className="orders-add-btn app-btn-primary"
                >
                  My completions
                </Button>
                <Button
                  type="primary"
                  icon={<TrophyOutlined />}
                  onClick={() => navigate('/my-rewards')}
                  className="orders-add-btn app-btn-primary"
                >
                  My rewards
                </Button>
              </div>
            </div>
          </div>
        )}

        <Card className="my-tasks-card">
          <Table
            className="my-tasks-table"
            columns={columns}
            dataSource={tasks.map((row) => ({ ...row, key: row._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tasks`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            responsive
            scroll={{ x: 700 }}
          />
        </Card>
      </Space>

      <Modal
        title="Submit work result"
        open={!!submitWorkTask}
        onCancel={() => { setSubmitWorkTask(null); setWorkResult([]); setWorkDescription(''); }}
        footer={[
          <Button key="cancel" onClick={() => { setSubmitWorkTask(null); setWorkResult([]); setWorkDescription(''); }} className="app-btn-cancel">
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={submitWorkLoading} onClick={handleSubmitWork} className="app-btn-primary">
            Submit
          </Button>,
        ]}
        destroyOnClose
        width={520}
      >
        {submitWorkTask && (
          <>
            <p><strong>Task:</strong> {submitWorkTask.title}</p>
            {submitWorkTask.description && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Description</div>
                {isHtml(submitWorkTask.description) ? (
                  <div
                    className="my-tasks-view-description my-tasks-view-description-html"
                    dangerouslySetInnerHTML={{ __html: submitWorkTask.description }}
                  />
                ) : (
                  <div className="my-tasks-view-description" style={{ whiteSpace: 'pre-wrap' }}>
                    {submitWorkTask.description}
                  </div>
                )}
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Your description (optional)</div>
              <Input.TextArea
                placeholder="Add notes about your submission..."
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                rows={3}
                maxLength={2000}
                showCount
              />
            </div>
            <p style={{ marginBottom: 16 }}>Upload proof (screenshots, links, or files) for admin to verify.</p>
            <Upload
              multiple
              maxCount={MAX_WORK_RESULT_FILES}
              accept="*/*"
              listType="text"
              fileList={workResult.map((a, i) => ({ uid: String(i), name: a.name, status: 'done' }))}
              beforeUpload={async (file) => {
                if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                  dispatch(setMessage({ message: `File "${file.name}" is larger than ${MAX_FILE_SIZE_MB}MB`, type: 'error' }));
                  return Upload.LIST_IGNORE;
                }
                try {
                  const { url, name } = await uploadFile(file);
                  setWorkResult((prev) => [...prev.slice(0, MAX_WORK_RESULT_FILES - 1), { name: name || file.name, url }]);
                } catch (err) {
                  dispatch(setMessage({ message: 'File upload failed', type: 'error' }));
                }
                return false;
              }}
              onRemove={(file) => {
                const idx = workResult.findIndex((_, i) => String(i) === file.uid);
                if (idx !== -1) setWorkResult((prev) => prev.filter((_, i) => i !== idx));
              }}
            >
              <Button icon={<PaperClipOutlined />}>Add file</Button>
            </Upload>
          </>
        )}
      </Modal>

      <Modal
        title={viewTask ? viewTask.title : 'Task details'}
        open={!!viewTask}
        onCancel={() => setViewTask(null)}
        footer={[
          <Button key="close" onClick={() => setViewTask(null)} className="app-btn-cancel">
            Close
          </Button>,
        ]}
        destroyOnClose
        width={520}
        className="my-tasks-view-modal"
      >
        {viewTask && (
          <div className="my-tasks-view">
            <div className="my-tasks-view-section">
              <div className="my-tasks-view-section-title">Description</div>
              {viewTask.description && isHtml(viewTask.description) ? (
                <div
                  className="my-tasks-view-description my-tasks-view-description-html"
                  dangerouslySetInnerHTML={{ __html: viewTask.description }}
                />
              ) : (
                <div
                  className="my-tasks-view-description"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {viewTask.description || '—'}
                </div>
              )}
            </div>
            <div className="my-tasks-view-section">
              <div className="my-tasks-view-section-title">Details</div>
              <div className="my-tasks-view-row">
                <span className="my-tasks-view-label">Reward points</span>
                <span className="my-tasks-view-value">
                {viewTask.rewardPoint != null && viewTask.rewardPoint !== 0
                  ? viewTask.rewardPoint
                  : 'None'}
              </span>
              </div>
              <div className="my-tasks-view-row">
                <span className="my-tasks-view-label">Completed date</span>
                <span className="my-tasks-view-value">{formatDate(viewTask.completedDate)}</span>
              </div>
              <div className="my-tasks-view-row">
                <span className="my-tasks-view-label">Status</span>
                <span className="my-tasks-view-value">
                  <Tag color="blue">
                    {STATUS_OPTIONS.find((o) => o.value === viewTask.status)?.label ??
                      viewTask.status}
                  </Tag>
                </span>
              </div>
            </div>
            {Array.isArray(viewTask.attachments) && viewTask.attachments.length > 0 && (
              <div className="my-tasks-view-section">
                <div className="my-tasks-view-section-title">Attachments</div>
                <ul className="my-tasks-view-attachments">
                  {viewTask.attachments.map((att, i) => (
                    <li key={i}>
                      <a
                        href={isImageAttachment(att) ? '#' : (att.url?.startsWith('data:') ? '#' : att.url)}
                        target={isImageAttachment(att) || att.url?.startsWith('data:') ? undefined : '_blank'}
                        rel={isImageAttachment(att) || att.url?.startsWith('data:') ? undefined : 'noopener noreferrer'}
                        className="my-tasks-view-attachment-link"
                        onClick={(e) => {
                          if (isImageAttachment(att)) {
                            e.preventDefault();
                            setImagePreview({ url: att.url, name: att.name });
                          } else if (att.url?.startsWith('data:')) {
                            e.preventDefault();
                            openAttachmentUrl(att.url);
                          }
                        }}
                      >
                        <PaperClipOutlined /> {att.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={imagePreview?.name ?? 'Image'}
        open={!!imagePreview}
        onCancel={() => setImagePreview(null)}
        footer={[
          <Button key="close" onClick={() => setImagePreview(null)} className="app-btn-cancel">
            Close
          </Button>,
        ]}
        width="min(90vw, 800px)"
        className="my-tasks-image-preview-modal"
        destroyOnClose
      >
        {imagePreview && (
          <div className="my-tasks-image-preview-wrap">
            <img
              src={imagePreview.url}
              alt={imagePreview.name}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
